import * as React from 'react'
import * as PubNub from 'pubnub'
import { CATEGORIES, StatusEvent } from 'pubnub'

import AdminAccount from '../../models/AdminAccount'
import PubNubService from '../../services/PubNubService'
import { newObjectId } from '../../util/objectId'

import {
    checkPatientOnlineStatus,
    fetchAuthKey,
    fetchClaimedChats,
    fetchLatestChats,
    fetchNewerChatMsgs,
    handleEvent,
    handleMessage,
    patientRejoinedChat,
    receiveChatMessage,
    resetToClaimed,
    setMessagePreview,
    setNetworkState,
    setPatientOnlineStatus,
} from './actions'
import { isPatientUuid } from './utils'

const { REACT_APP_SMILE_SPECIALIST_CHANNEL } = process.env

export type PubNubEventManagerProps = {
    account: AdminAccount
    authKey: string | null
    claimedChats?: { [channel: string]: Models.ChatMetadata }
    selectedChats: Models.ChatMetadata[] | null
    conversations: { [channel: string]: Models.Chat }
}

export type PubNubEventManagerDispatch = {
    fetchAuthKey: typeof fetchAuthKey
    fetchLatestChats: typeof fetchLatestChats
    fetchClaimedChats: typeof fetchClaimedChats
    handleEvent: typeof handleEvent
    handleMessage: typeof handleMessage
    setMessagePreview: typeof setMessagePreview
    fetchNewerChatMsgs: typeof fetchNewerChatMsgs
    setNetworkState: typeof setNetworkState
    patientRejoinedChat: typeof patientRejoinedChat
    setPatientOnlineStatus: typeof setPatientOnlineStatus
    checkPatientOnlineStatus: typeof checkPatientOnlineStatus
    receiveChatMessage: typeof receiveChatMessage
    resetToClaimed: typeof resetToClaimed
}

export type PubNubEventManagerState = {}

type Props = PubNubEventManagerProps & PubNubEventManagerDispatch

function isEvent(
    message: PubNub.MessageEvent<Services.PubNub.MessageEvent>,
): message is PubNub.MessageEvent<Services.PubNub.Event> {
    return message.channel === REACT_APP_SMILE_SPECIALIST_CHANNEL
}

class PubNubEventManager extends React.Component<Props, PubNubEventManagerState> {
    listener: PubNub.ListenerParameters<Services.PubNub.MessageEvent, Services.PubNub.Presence>
    // Local departure evidence; a first observed join is not proof that a chatter rejoined.
    departedChatters = new Map<string, Set<string>>()

    constructor(props: Props) {
        super(props)

        this.listener = {
            message: this.onMessage,
            presence: this.onPresence,
            status: this.onStatus,
        }
    }

    componentDidMount() {
        this.props.fetchAuthKey()

        const pubnubService = PubNubService.getInstance(this.props.account.id)
        pubnubService.setChatterId(this.props.account.id)
        this.props.fetchLatestChats()
        pubnubService.addListener(this.listener)
    }

    componentDidUpdate(prevProps: Props) {
        const selectedChannels = new Set(this.props.selectedChats?.map(chat => chat.channelName) || [])
        this.departedChatters.forEach((_, channelName) => {
            if (!selectedChannels.has(channelName)) {
                this.departedChatters.delete(channelName)
            }
        })

        if (this.props.authKey && prevProps.authKey !== this.props.authKey) {
            const pubnubService = PubNubService.getInstance(this.props.account.id)
            pubnubService.setToken(this.props.authKey)
            pubnubService.subscribe([REACT_APP_SMILE_SPECIALIST_CHANNEL as string], false)
            this.props.fetchClaimedChats()
        }
    }

    componentWillUnmount() {
        this.departedChatters.clear()
        const pubnubService = PubNubService.getInstance(this.props.account.id)
        pubnubService.unsubscribeAll()
        pubnubService.removeListener(this.listener)
    }

    onPresence = (presence: PubNub.PresenceEvent<Services.PubNub.Presence>) => {
        if (presence && presence.state && presence.state.preview != null) {
            this.props.setMessagePreview(presence.channel, presence.state.preview)
        }

        if (presence?.action === 'leave') {
            this.checkIfPatientOrChatterLeftChat(presence)
        }

        if (presence?.action === 'timeout') {
            this.recordChatterDeparture(presence)
        }

        if (presence?.action === 'join') {
            this.checkIfPatientOrChatterRejoinedChat(presence)
        }
    }

    // Derives the left-chat notice locally from the same `leave` presence event the still-live
    // `Presence` PubNub Function publishes (`Presence.js:60-79`), rather than waiting on its wire
    // publish - mirroring the rejoin notice above. `type`/`text`/`is_patient`/`sender_id` match the
    // Function's payload on every field a reader can see; `id` is 32 chars (a React key / `uniqBy`
    // key only, matching the rejoin precedent) rather than the Function's 36, and `timetoken` is a
    // client-clock stamp plus `localOnly: true` so the feed filter (`ChatConversation.getMessages()`)
    // and `lastServerTimetoken` can tell this row apart from the Function's own wire row (S5 X2).
    // Gated on `action === 'leave'` only - a `timeout` (tab closed, no graceful unsubscribe) produces
    // no notice today on either side, and widening to it would be new behavior (S5 X10). Also ports
    // all three clauses of `Presence.js:60`'s own guard, for the same reason: PST must not render
    // notices the Function itself would never publish. `uuid.length <= 16` - a patient session id is
    // always 16 characters and a specialist account id is always exactly 16 (`Presence.js`'s own
    // resolved comment), so both actor classes always pass; only something with a longer uuid (e.g.
    // a UUID v4) would get silently skipped by the Function. `channels[0] != 'amp.*-pnpres'` - `025-`
    // M4: this clause was missing from the original port, so PST could derive notices for presence
    // traffic the Function deliberately declines, including the shared `admin-server` capture
    // worker's reconnect (S5 X9).
    checkIfPatientOrChatterLeftChat = (presence: PubNub.PresenceEvent<Services.PubNub.Presence>) => {
        const channelName = presence.channel
        const { selectedChats } = this.props
        const chat = selectedChats?.find(chat => chat.channelName === channelName)

        if (!chat || presence.uuid.length > 16 || channelName === 'amp.*-pnpres') {
            return
        }

        if (isPatientUuid(channelName, presence.uuid)) {
            this.props.setPatientOnlineStatus(chat, false)
            this.renderLeftChatNotification(
                channelName,
                presence.uuid,
                'Patient left chat.',
                'system:patient-left-chat',
            )
        } else {
            this.recordChatterDeparture(presence)
            // Deliberately unconditional, matching `Presence.js:73`'s own logic: any uuid that isn't
            // the patient (a self-leave, another specialist, or the capture worker's `admin-server`
            // uuid - S5 X9) renders "Chatter left chat.". This is intentional parity, not a missing
            // check - do not add narrower conditions here without re-deriving them from the Function.
            this.renderLeftChatNotification(
                channelName,
                presence.uuid,
                'Chatter left chat.',
                'system:chatter-left-chat',
            )
        }
    }

    recordChatterDeparture = (presence: PubNub.PresenceEvent<Services.PubNub.Presence>) => {
        const { channel, uuid } = presence
        if (!this.props.selectedChats?.some(chat => chat.channelName === channel) || isPatientUuid(channel, uuid)) {
            return
        }

        const departed = this.departedChatters.get(channel) || new Set<string>()
        departed.add(uuid)
        this.departedChatters.set(channel, departed)
    }

    consumeChatterDeparture = (channelName: string, uuid: string): boolean => {
        const departed = this.departedChatters.get(channelName)
        if (!departed?.delete(uuid)) {
            return false
        }

        if (departed.size === 0) {
            this.departedChatters.delete(channelName)
        }
        return true
    }

    // `025-` M3: `ChatConversation.getMessages()` sorts the whole transcript by raw `timetoken`,
    // including this client-clock stamp - `lastServerTimetoken` only guards the pagination cursor,
    // nothing guards this display sort. A skewed local clock could place a leave/rejoin notice at
    // the wrong end of the conversation, the same class of trap as
    // `[[local-dev-future-stamped-chat-trap]]`. A leave/rejoin can only happen after every message
    // this client has already seen on the channel, so clamp the stamp to be at least one past the
    // latest wire timetoken already known for this channel - this only corrects the case where the
    // local clock reads *behind* the server (which would otherwise misorder the notice into the
    // middle of real history); a clock reading *ahead* still sorts the notice last, which is where a
    // just-happened event belongs anyway.
    nextLocalTimetoken = (channelName: string): string => {
        const messages = this.props.conversations[channelName]?.messages || []
        const latestWireTimetoken = messages
            .filter(message => !message.localOnly)
            .reduce((max, message) => Math.max(max, Number(message.timetoken)), 0)

        return String(Math.max(Date.now() * 10000, latestWireTimetoken + 1))
    }

    renderLeftChatNotification = (
        channelName: string,
        senderId: string,
        text: string,
        type: 'system:patient-left-chat' | 'system:chatter-left-chat',
    ) => {
        this.props.receiveChatMessage(channelName, {
            id: newObjectId(32),
            text,
            is_patient: false,
            sender_id: senderId,
            type,
            timetoken: this.nextLocalTimetoken(channelName),
            localOnly: true,
        })
    }

    // Presence-driven reconnect notifications are rendered locally rather than published to the
    // conversation channel (Rich's doc-34 F1 disposition: non-authoritative presence/control
    // notifications, not sequenced conversation content, so client publish authority is removed).
    // Every specialist subscribed to this channel (`withPresence: true`) receives the same presence
    // event independently, so a confirmed rejoin renders for every viewer regardless of claimee status.
    // `patientRejoinedChat` (Connect payment re-request)
    // stays claimee-only, unchanged. `resetToClaimed` is also claimee-only: it reaches a server
    // status write (`PUT /chats/:id`), and the idle/inactive timers it clears only ever exist on
    // the claimee's own client (set solely when the claimee is the one who sent the last message).
    // A non-claimee viewer has no timer to clear and no standing to write this chat's status.
    // Further gated on `chat.status.value === 'idle'` (Rich's presence-slice correction): a reconnect
    // should only replay the recovery a fresh message already causes from idle - at that point the
    // idle timer has already fired, and it's the still-pending inactive timer this call needs to
    // cancel. `claimed` needs no recovery, and `inactive` is a deliberately deeper state a presence
    // blip should not undo. This `idle` gate depends on `idle` being reachable at all, which in turn
    // depends on the arming predicate at `actions.tsx:856` - this rejoin recovery was silently
    // unreachable for the same reason the arming was, from the admission cutover until 61- restored it.
    renderRejoinNotification = (
        channelName: string,
        text: string,
        type: 'system:patient-rejoined-chat' | 'system:chatter-rejoined-chat',
    ) => {
        const pubnubService = PubNubService.getInstance(this.props.account.id)
        this.props.receiveChatMessage(channelName, {
            id: newObjectId(32),
            text,
            is_patient: false,
            sender_id: pubnubService.getChatterId(),
            type,
            timetoken: this.nextLocalTimetoken(channelName),
            localOnly: true,
        })
    }

    checkIfPatientOrChatterRejoinedChat = (presence: PubNub.PresenceEvent<Services.PubNub.Presence>) => {
        const channelName = presence.channel
        const { selectedChats } = this.props
        const chat = selectedChats?.find(chat => chat.channelName === channelName)
        const currentUserIsClaimee = this.props.account.id === chat?.claimee?.id
        const isPatient = isPatientUuid(channelName, presence.uuid)
        if (chat && isPatient) {
            this.props.setPatientOnlineStatus(chat, true)
        }
        if (chat && isPatient) {
            if (currentUserIsClaimee) {
                this.props.patientRejoinedChat(chat)

                if (chat.status.value === 'idle') {
                    this.props.resetToClaimed(chat)
                }
            }

            this.renderRejoinNotification(channelName, 'Patient rejoined chat.', 'system:patient-rejoined-chat')
        }

        if (chat && !isPatient) {
            this.props.checkPatientOnlineStatus(chat)
        }
        if (chat && !isPatient) {
            if (currentUserIsClaimee) {
                this.props.patientRejoinedChat(chat)

                // presence.uuid identifies who generated the join; this.props.account.id is this
                // viewer (already confirmed to be the claimee by currentUserIsClaimee above).
                // Without this check any bystander opening the chat - not closing it, just
                // opening it - silently clears the claimee's own idle/inactive triage signal.
                if (chat.status.value === 'idle' && presence.uuid === this.props.account.id) {
                    this.props.resetToClaimed(chat)
                }
            }

            // Only the notice needs departure evidence; recovery checks above still run on every join.
            if (this.consumeChatterDeparture(channelName, presence.uuid)) {
                this.renderRejoinNotification(channelName, 'Chatter rejoined chat.', 'system:chatter-rejoined-chat')
            }
        }
    }

    onMessage = (message: PubNub.MessageEvent<Services.PubNub.MessageEvent>) => {
        const { claimedChats } = this.props
        const chat = claimedChats && claimedChats[message.channel]

        return isEvent(message)
            ? this.props.handleEvent(message)
            : this.props.handleMessage(message as PubNub.MessageEvent<Services.PubNub.Message>, chat)
    }

    onStatus = (statusEvent: StatusEvent) => {
        const { selectedChats, conversations } = this.props
        const pubnubService = PubNubService.getInstance(this.props.account.id)

        if (statusEvent.category === CATEGORIES.PNAccessDeniedCategory) {
            this.props.fetchAuthKey()
            return
        }

        if (statusEvent.category === CATEGORIES.PNNetworkUpCategory && selectedChats) {
            selectedChats.forEach(selectedChat => {
                this.props.fetchNewerChatMsgs(selectedChat.channelName, conversations[selectedChat.channelName])
                const whisperChannelName = `whisper_${selectedChat.channelName}`
                this.props.fetchNewerChatMsgs(whisperChannelName, conversations[whisperChannelName])
                pubnubService.subscribe([selectedChat.channelName, `whisper_${selectedChat.channelName}`])
            })
        }

        if (statusEvent.category === CATEGORIES.PNNetworkUpCategory) {
            pubnubService.subscribe([REACT_APP_SMILE_SPECIALIST_CHANNEL as string], false)
            this.props.fetchLatestChats()
            this.props.setNetworkState(true)
        }

        if (statusEvent.category === CATEGORIES.PNNetworkDownCategory) {
            this.props.setNetworkState(false)
        }
    }

    render() {
        return <div />
    }
}

export default PubNubEventManager
